import { useState, lazy, Suspense, useEffect } from "react";
import Dashboard from "./pages/Dashboard";
import { ToastProvider } from "./components/ToastProvider";
import ProfileCompletionModal from "./components/ProfileCompletionModal";
import ForcePasswordChangeModal from "./components/ForcePasswordChangeModal";
import { LogoContext } from "./context/LogoContext";
import OfflineBanner from "./components/OfflineBanner";
import SyncStatusBanner from "./components/SyncStatusBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { applyProfessorTheme, applyPwaManifest } from "./utils/theme";
import { mountTooltip } from "./utils/tooltip";
import { useTheme } from "./hooks/useTheme";
import { refreshToken } from "./services/auth";
import { startOutbox } from "./services/workoutOutbox";
import "./styles/theme.css";  /* Design System — Tokens Globais */
import "./App.css";

const Login = lazy(() => import("./pages/Login"));
const SignUp = lazy(() => import("./pages/SignUp"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PublicChallengePage = lazy(() => import("./pages/PublicChallengePage"));
const OpenAppBridge = lazy(() => import("./pages/OpenAppBridge"));
const DebugPayments = lazy(() => import("./pages/DebugPayments")); // rota de teste — só ativa em build de desenvolvimento (ver gate abaixo)


function Splash({ label = "Inicializando aplicação…" }) {
  return (
    <div className="app-splash">
      <div className="app-splash-card">
        <div className="app-splash-spinner" />
        <div className="app-splash-text">{label}</div>
      </div>
    </div>
  );
}

function App() {
  // Página pública de desafio (Fase 6 — audiência): /desafio/:id, sem login
  const publicChallengeId = (() => {
    const m = window.location.pathname.match(/^\/desafio\/(\d+)/);
    return m ? m[1] : null;
  })();

  // Cadastro público self-service (auto-onboarding aluno/professor, ver
  // app/api/auth_register.py e SignUp.js) — igual a /desafio/:id, acessível
  // sem sessão e checado antes do gate de login.
  // ?perfil=aluno|professor na raiz (links antigos da landing, WhatsApp,
  // Instagram) também abre o cadastro em vez do login; a URL vira /cadastro
  // com os mesmos parâmetros (ver efeito abaixo). Quem já tem sessão segue
  // pro app normalmente.
  const perfilParam = new URLSearchParams(window.location.search).get("perfil");
  const wantsSignUp = window.location.pathname === "/" && (perfilParam === "aluno" || perfilParam === "professor");
  const isSignUpPath = window.location.pathname === "/cadastro" || wantsSignUp;

  // Link de confirmação de email (ver email_verification_template.py) —
  // também público, checado antes do gate de login.
  const verifyEmailToken = (() => {
    const m = window.location.pathname.match(/^\/verificar-email\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  })();

  // "Esqueci minha senha" (ver app/api/auth_password_reset.py) — mesmo
  // padrão de rota pública dos dois de cima, checado antes do gate de login.
  const isForgotPasswordPath = window.location.pathname === "/esqueci-senha";
  const resetPasswordToken = (() => {
    const m = window.location.pathname.match(/^\/redefinir-senha\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  })();

  const [user, setUser] = useState(null);

  // Ajusta a barra de endereço pra /cadastro quando o cadastro foi aberto
  // pela raiz com ?perfil= (ver wantsSignUp), mantendo os parâmetros.
  useEffect(() => {
    if (wantsSignUp && !user) {
      window.history.replaceState(null, "", "/cadastro" + window.location.search);
    }
  }, [wantsSignUp, user]);

  const [checkingSession, setCheckingSession] = useState(true);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const init = async () => {
      const savedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");
      const storedRefresh = localStorage.getItem("refreshToken");
      const expiresAt = Number(localStorage.getItem("expiresAt"));

      const isExpired = !expiresAt || Date.now() >= expiresAt;

      const maybeAskProfile = (u) => {
        if (u.role !== "google" && (!u.gender || !u.birth_date || !u.height_cm || !u.activity_level)) {
          setShowProfileCompletion(true);
        }
      };

      const restoreUser = (parsed) => {
        // Mostra imediatamente o usuário do cache (boot rápido)...
        setUser(parsed);
        maybeAskProfile(parsed);
        // ...e revalida contra o servidor. O cache do localStorage é gravado no
        // login e fica DESATUALIZADO quando o backend muda o usuário sem novo
        // login — ex.: self-service que gera o treino e vira 'aluno'. Sem isso,
        // depois de um F5 o app ainda lê role='google' do cache e reabre o
        // onboarding, mesmo com as fichas já criadas no banco. Ver docs/07-issues.md.
        const _tok = localStorage.getItem("token");
        if (_tok) {
          fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
            headers: { Authorization: `Bearer ${_tok}` },
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((fresh) => {
              if (!fresh || !fresh.id) return;
              setUser(fresh);
              localStorage.setItem("user", JSON.stringify(fresh));
              maybeAskProfile(fresh);
            })
            .catch(() => { /* rede/instabilidade — mantém o cache, não desloga */ });
        }
      };

      if (savedUser && token && !isExpired) {
        // Token still valid — use it directly
        restoreUser(JSON.parse(savedUser));
      } else if (savedUser && storedRefresh) {
        // Access token expired but refresh token available — try to renew silently.
        // Achado da revisão de sessão (06/09/2026): se essa renovação falhar por
        // rede (sem internet no boot, backend fora do ar), NÃO apaga a sessão —
        // mantém o usuário no cache (abordagem offline-first) e deixa o
        // interceptor do api.js tentar de novo na primeira requisição real.
        // Só apaga quando o backend responde e rejeita o refresh token de fato.
        try {
          const newToken = await refreshToken();
          if (newToken) {
            restoreUser(JSON.parse(savedUser));
          } else {
            // Refresh rejeitado pelo backend (expirado/revogado) — força login
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("expiresAt");
            localStorage.removeItem("refreshToken");
          }
        } catch (_networkErr) {
          restoreUser(JSON.parse(savedUser));
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("expiresAt");
        localStorage.removeItem("refreshToken");
      }

      setCheckingSession(false);
    };

    init();
  }, []);

  // Aplica cor de marca do professor sempre que o usuário ou o tema mudar
  useEffect(() => {
    applyProfessorTheme(user?.professor_brand_color ?? user?.own_brand_color ?? null, theme, {
      dark: user?.professor_bg_color_dark ?? user?.own_bg_color_dark ?? null,
      light: user?.professor_bg_color_light ?? user?.own_bg_color_light ?? null,
    });
  }, [
    user?.professor_brand_color, user?.own_brand_color,
    user?.professor_bg_color_dark, user?.own_bg_color_dark,
    user?.professor_bg_color_light, user?.own_bg_color_light,
    theme,
  ]);

  // Ícone do PWA ("Adicionar à tela inicial") — troca o manifest/apple-touch-icon
  // pro ícone customizado do professor (ou herdado, se aluno), quando existir.
  useEffect(() => {
    applyPwaManifest(
      user?.role, user?.id, user?.professor_id,
      user?.custom_app_icon_url, user?.professor_app_icon_url,
      process.env.REACT_APP_API_URL,
      user?.name, user?.professor_name
    );
  }, [user?.role, user?.id, user?.professor_id, user?.professor_app_icon_url, user?.custom_app_icon_url, user?.name, user?.professor_name]);

  // Tooltip global — funciona dentro de overflow:hidden (posição fixed no body)
  useEffect(() => {
    const cleanup = mountTooltip();
    return cleanup;
  }, []);

  // Fila de sincronização do treino — retoma o que ficou pendente de uma
  // sessão anterior do app (rede caiu na academia, usuário fechou o app
  // antes de voltar a conexão). Ver services/workoutOutbox.js.
  useEffect(() => {
    startOutbox();
  }, []);

  // Rota de demonstração removida; validação agora é feita no próprio Dashboard

  const handleLogin = (userData) => {

    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    // Mostra popup se perfil incompleto
    // Usuários google ainda não são alunos — não pedir completar perfil agora
    if (userData.role !== "google" && (!userData.gender || !userData.birth_date || !userData.height_cm || !userData.activity_level)) {
      setShowProfileCompletion(true);
    }
  };

  const handleLogout = (silent = false) => {
    // Confirmação visual é tratada nos componentes de UI (Dashboard/GoogleWelcome)
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("expiresAt");
    localStorage.removeItem("refreshToken");

    setUser(null);
  };

  // Página-ponte dos emails (/abrir, ver app/utils/email_links.py): não
  // depende de sessão, então aparece antes da checagem de login.
  if (window.location.pathname === "/abrir") {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Splash label="Carregando…" />}>
          <OpenAppBridge />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (checkingSession) return <Splash label="Inicializando aplicação…" />;

  // Página pública de desafio — acessível sem login (funil de audiência)
  if (publicChallengeId) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Splash label="Carregando desafio…" />}>
          <PublicChallengePage challengeId={publicChallengeId} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Cadastro público — acessível sem login. Mesma posição/padrão de
  // publicChallengeId acima: roda depois do gate de checkingSession (não
  // antes), então uma sessão salva ainda é restaurada primeiro — evita
  // achatar quem tem sessão válida e só bateu em /cadastro por engano.
  if (isSignUpPath && !user) {
    return (
      <ErrorBoundary>
        <ToastProvider>
          <Suspense fallback={<Splash label="Carregando cadastro…" />}>
            <SignUp onLogin={handleLogin} />
          </Suspense>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // Confirmação de email — pública, independente de sessão (quem clica no
  // link pode ou não estar logado no mesmo navegador)
  if (verifyEmailToken) {
    return (
      <ErrorBoundary>
        <ToastProvider>
          <Suspense fallback={<Splash label="Confirmando email…" />}>
            <VerifyEmail token={verifyEmailToken} />
          </Suspense>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // "Esqueci minha senha" — pedido do link (pública) e uso do link
  // (também pública, independente de sessão — mesmo raciocínio de
  // verifyEmailToken acima).
  if (isForgotPasswordPath) {
    return (
      <ErrorBoundary>
        <ToastProvider>
          <Suspense fallback={<Splash label="Carregando…" />}>
            <ForgotPassword />
          </Suspense>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  if (resetPasswordToken) {
    return (
      <ErrorBoundary>
        <ToastProvider>
          <Suspense fallback={<Splash label="Carregando…" />}>
            <ResetPassword token={resetPasswordToken} />
          </Suspense>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  if (!user) {
    return (
      <ErrorBoundary>
        <ToastProvider>
          <Suspense fallback={<Splash label="Carregando login…" />}>
            <Login onLogin={handleLogin} />
          </Suspense>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // NODE_ENV é "production" no build do CRA (Dockerfile.prod) — a rota de
  // debug de pagamentos só existe em desenvolvimento.
  if (window.location.pathname === "/debug/payments" && process.env.NODE_ENV !== "production") {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Splash label="Carregando…" />}>
          <DebugPayments />
        </Suspense>
      </ErrorBoundary>
    );
  }

  const profileIncomplete = user && (!user.gender || !user.birth_date || !user.height_cm || !user.activity_level);

  // Logo personalizado do professor — mesmo cálculo usado dentro do Dashboard,
  // mas levantado aqui pra alcançar também os modais que ficam fora da árvore
  // do Dashboard (ProfileCompletionModal, ForcePasswordChangeModal). Sem isso,
  // esses modais caem no LogoContext padrão (null) e mostram o logo genérico
  // do TreinoNexo mesmo quando o professor tem logo próprio configurado.
  const apiUrl = process.env.REACT_APP_API_URL;
  // Objeto {dark, light} -- mesmo formato do cálculo espelhado em
  // Dashboard.js (LogoContext agora carrega as duas variantes, R248).
  const professorLogo = {
    dark: user?.professor_logo_url ? `${apiUrl}/${user.professor_logo_url}` : null,
    light: user?.professor_logo_light_url ? `${apiUrl}/${user.professor_logo_light_url}` : null,
  };

  return (
    <ErrorBoundary>
      <OfflineBanner />
      <SyncStatusBanner />
      <ToastProvider>
        <LogoContext.Provider value={professorLogo}>
          <Dashboard
            user={user}
            onLogout={handleLogout}
            onUserUpdate={(updates) => {
              const merged = { ...user, ...updates };
              localStorage.setItem("user", JSON.stringify(merged));
              setUser(merged);
            }}
            onThemeChange={setTheme}
            currentTheme={theme}
          />
          {user.must_change_password ? (
            <ForcePasswordChangeModal
              user={user}
              onChanged={(patch) => {
                const merged = { ...user, ...patch };
                localStorage.setItem("user", JSON.stringify(merged));
                setUser(merged);
              }}
            />
          ) : (
            showProfileCompletion && profileIncomplete && (
              <ProfileCompletionModal
                user={user}
                onSaved={(updated) => {
                  const merged = { ...user, ...updated };
                  localStorage.setItem("user", JSON.stringify(merged));
                  setUser(merged);
                  setShowProfileCompletion(false);
                }}
                onSkip={() => setShowProfileCompletion(false)}
              />
            )
          )}
        </LogoContext.Provider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;